import { useCallback, useEffect, useRef } from "react";

import { PDF_BASE_URL } from "../../config";

const AI_EXPLAINED_AUDIO_BASE_URL =
  `${PDF_BASE_URL}/static/audio/ai_explained/openai_echo_ai_explained`;

const NAVIGATION_NARRATION_OWNER = Symbol("ai-exhibit-navigation-narration");

let activeAudio = null;
let activeOwner = null;

function buildNarrationUrl(language, narrationId) {
  const safeLanguage = language === "es" ? "es" : "en";
  return `${AI_EXPLAINED_AUDIO_BASE_URL}/${safeLanguage}/${narrationId}.mp3`;
}

function stopActiveNarration(requestingOwner = null) {
  if (
    requestingOwner !== null &&
    activeOwner !== requestingOwner
  ) {
    return;
  }

  const audio = activeAudio;

  if (!audio) {
    if (requestingOwner === null) {
      activeOwner = null;
    }
    return;
  }

  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (error) {
    console.warn("AI Explained narration stop failed:", error);
  }

  activeAudio = null;
  activeOwner = null;
}

function playActiveNarration({
  audioEnabled = true,
  language = "en",
  narrationId,
  owner,
}) {
  if (!audioEnabled || !narrationId) {
    return;
  }

  stopActiveNarration();

  if (typeof Audio === "undefined") {
    console.warn("AI Explained narration requires browser Audio support.");
    return;
  }

  const audio = new Audio(buildNarrationUrl(language, narrationId));
  audio.preload = "auto";

  activeAudio = audio;
  activeOwner = owner;

  audio.onended = () => {
    if (activeAudio === audio) {
      activeAudio = null;
      activeOwner = null;
    }
  };

  audio.onerror = () => {
    if (activeAudio === audio) {
      activeAudio = null;
      activeOwner = null;
    }

    console.warn(
      `AI Explained narration failed to load: ${language}/${narrationId}`
    );
  };

  const playPromise = audio.play();

  if (playPromise?.catch) {
    playPromise.catch((error) => {
      if (activeAudio === audio) {
        activeAudio = null;
        activeOwner = null;
      }

      console.warn("AI Explained narration playback failed:", error);
    });
  }
}

export function playAIExhibitFixedNarration({
  audioEnabled = true,
  language = "en",
  narrationId,
}) {
  playActiveNarration({
    audioEnabled,
    language,
    narrationId,
    owner: NAVIGATION_NARRATION_OWNER,
  });
}

export function stopAIExhibitFixedNarration() {
  stopActiveNarration();
}

export function useAIExhibitFixedNarration({
  audioEnabled = false,
  language = "en",
}) {
  const ownerRef = useRef(null);

  if (!ownerRef.current) {
    ownerRef.current = {};
  }

  const stopNarration = useCallback(() => {
    stopActiveNarration(ownerRef.current);
  }, []);

  const playNarration = useCallback(
    (narrationId) => {
      playActiveNarration({
        audioEnabled,
        language,
        narrationId,
        owner: ownerRef.current,
      });
    },
    [audioEnabled, language]
  );

  useEffect(() => {
    if (!audioEnabled) {
      stopNarration();
    }
  }, [audioEnabled, stopNarration]);

  useEffect(() => {
    stopNarration();
  }, [language, stopNarration]);

  useEffect(() => stopNarration, [stopNarration]);

  return {
    playNarration,
    stopNarration,
  };
}
